import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

const ICON_MAP = {
  info: 'bi-info-circle',
  warning: 'bi-exclamation-triangle',
  error: 'bi-x-circle',
  success: 'bi-check-circle',
  task: 'bi-list-task',
  approval: 'bi-clipboard-check',
  // Tool module events (#90 / #96)
  tool_assigned: 'bi-tools',
  tool_picked_up_self: 'bi-shop',
  tool_sub_assigned_received: 'bi-arrow-right-circle',
  tool_sub_assigned_broadcast: 'bi-share',
  tool_returned_to_me: 'bi-arrow-left-circle',
  tool_returned_to_warehouse: 'bi-box-arrow-in-left',
  tool_damaged_reported: 'bi-exclamation-octagon',
  tool_lost_reported: 'bi-x-octagon',
  tool_maintenance_reported: 'bi-wrench-adjustable',
  // Tool request workflow (#105)
  tool_request_created: 'bi-clipboard-plus',
  tool_request_approved: 'bi-check2-circle',
  tool_request_rejected: 'bi-x-circle',
  tool_request_fulfilled: 'bi-tools',
  tool_request_cancelled: 'bi-slash-circle'
};

const COLOR_MAP = {
  info: '#0d6efd',
  warning: '#ffc107',
  error: '#dc3545',
  success: '#198754',
  task: '#0057B8',
  approval: '#6f42c1',
  tool_assigned: '#0057B8',
  tool_picked_up_self: '#198754',
  tool_sub_assigned_received: '#0d6efd',
  tool_sub_assigned_broadcast: '#6f42c1',
  tool_returned_to_me: '#198754',
  tool_returned_to_warehouse: '#0d6efd',
  tool_damaged_reported: '#fd7e14',
  tool_lost_reported: '#dc3545',
  tool_maintenance_reported: '#ffc107',
  tool_request_created: '#6f42c1',
  tool_request_approved: '#198754',
  tool_request_rejected: '#dc3545',
  tool_request_fulfilled: '#0057B8',
  tool_request_cancelled: '#6b7280'
};

/**
 * Allowlist de rutas a las que una notificacion PUEDE navegar (L08).
 *
 * Hasta acá el componente hacia `navigate(deepLink)` con cualquier string que
 * viniera en `action_url`/`metadata.deep_link`. En produccion eso significaba
 * que el 67% de las notificaciones (11.368 de 16.961, medido 2026-07-30)
 * mandaba al usuario a `/herramientas/solicitudes`, una ruta que no existe:
 * React Router la matcheaba contra `/herramientas/:id`, `ToolDetail` intentaba
 * leer una herramienta con id="solicitudes" y el resultado era un 400 22P02
 * (si el que clickeaba era admin) o un falso "Acceso restringido a
 * administradores" (si era supervisor — o sea el destinatario principal de esa
 * notificacion, al que se le enseñaba que aprobar no le corresponde).
 *
 * `action_url` es ademas un parametro ABIERTO del agente: `notify_user` /
 * `create_notification` lo aceptan tal cual del modelo. Un guard acá es lo
 * unico que separa "el modelo escribio mal una ruta" de "el usuario termina en
 * una pantalla rota".
 *
 * Cada entrada tiene que ser una ruta declarada en `src/App.js`. Lo verifica
 * `src/__tests__/unit/deepLinkNotificacionRutaReal.test.js`, que ademas cruza
 * esta lista contra todos los deep links literales que emite el codigo: si
 * alguien emite un path nuevo, el test se pone rojo hasta que la ruta exista
 * y entre acá.
 */
export const RUTAS_NAVEGABLES_NOTIFICACION = [
  '/dashboard',
  '/mensajes/:uuid',
  '/proyectos/:uuid',
  '/tareas/:uuid',
  '/herramientas',
  '/herramientas/mis-solicitudes',
  // L13 — la bandeja de aprobacion. Este path se emitia desde antes de que la
  // ruta existiera (era el 67% de las notificaciones); ahora existe y esta
  // gateada a admin+supervisor en src/App.js.
  '/herramientas/solicitudes',
  '/herramientas/:uuid',
  '/mis-herramientas',
];

/**
 * Los segmentos `:uuid` exigen forma de UUID, no "cualquier cosa".
 *
 * Esa distincion ES el bug: `/herramientas/solicitudes` matchea
 * `/herramientas/:id` en React Router porque `:id` acepta cualquier string, y
 * recien Postgres se da cuenta (22P02 "invalid input syntax for type uuid").
 * Verificado contra produccion el 2026-07-30: `tools.id`, `projects.id`,
 * `tasks.id` y `chat_conversations.id` son todos `uuid`, y las 3.508
 * notificaciones vivas con `/herramientas/<algo>` traen uuids reales. Un
 * segmento que no es uuid en esas rutas no es un id: es un typo.
 */
const RX_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `true` solo si `deepLink` es una ruta relativa de la app que existe en el
 * router. Rechaza absolutos (`https://`, `//host`), esquemas (`javascript:`),
 * traversal y cualquier forma que no este en la allowlist.
 */
export const esDeepLinkNavegable = (deepLink) => {
  if (typeof deepLink !== 'string' || deepLink.length === 0) return false;
  if (!deepLink.startsWith('/')) return false;   // relativo obligatorio
  if (deepLink.startsWith('//')) return false;   // protocol-relative -> otro origen
  if (deepLink.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(deepLink)) return false;

  const soloPath = deepLink.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (soloPath.split('/').includes('..')) return false;

  const segs = soloPath.split('/');
  return RUTAS_NAVEGABLES_NOTIFICACION.some((patron) => {
    const pat = patron.split('/');
    if (pat.length !== segs.length) return false;
    return pat.every((s, i) => (s === ':uuid' ? RX_UUID.test(segs[i]) : s === segs[i]));
  });
};

const timeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `hace ${diffD}d`;
  return date.toLocaleDateString('es-CR');
};

const NotificationItem = ({ notification, onClose }) => {
  const { markAsRead } = useNotifications();
  const navigate = useNavigate();
  const isUnread = !notification.read_at;
  const icon = ICON_MAP[notification.notification_type] || 'bi-bell';
  const color = COLOR_MAP[notification.notification_type] || '#6b7280';

  // Tool module events use metadata.deep_link instead of action_url
  const deepLinkCrudo = notification.action_url || notification.metadata?.deep_link || null;
  // Un deep link que no resuelve a una ruta real NO se navega: se muestra la
  // notificacion igual (el texto es la mitad util del aviso) pero sin ofrecer
  // un click que lleva a una pantalla rota. Ver RUTAS_NAVEGABLES_NOTIFICACION.
  const deepLink = esDeepLinkNavegable(deepLinkCrudo) ? deepLinkCrudo : null;

  const handleClick = async () => {
    // Marcar como leida NO depende del deep link: si no, una notificacion con
    // link roto quedaba sin forma de sacarse el punto azul de encima.
    if (isUnread) {
      await markAsRead([notification.id]);
    }
    if (deepLink) {
      navigate(deepLink);
      onClose?.();
    }
  };

  return (
    <div
      className={`notification-item ${isUnread ? 'notification-item-unread' : ''}`}
      onClick={handleClick}
      role={deepLink ? 'button' : undefined}
      tabIndex={deepLink ? 0 : undefined}
      style={deepLink ? undefined : { cursor: 'default' }}
    >
      <div className="notification-item-icon" style={{ color }}>
        <i className={`bi ${icon}`}></i>
      </div>
      <div className="notification-item-content">
        <div className="notification-item-title">{notification.title}</div>
        <div className="notification-item-message">{notification.message}</div>
        <div className="notification-item-time">{timeAgo(notification.created_at)}</div>
      </div>
      {isUnread && <div className="notification-item-dot"></div>}
    </div>
  );
};

export default NotificationItem;
